import S from "sanctuary";
import * as Nested from "@/util/Nested";
import { getPointOfSaleEnabled } from "../../state/client.state";
import {
  CREATE_ADMINISTERED_USER_SUCCEEDED,
  EDIT_ADMINISTERED_USER_SUCCEEDED,
  getAdministeredUserBeingEdited,
  getCheckAdministeredUserPending,
  getUserModificationStatuses,
  getRoleCombinations,
} from "@/components/administeredUsers/AdministeredUsers.state";
import InternalUserInfoForm from "@/components/administeredUsers/InternalUserInfoForm";
import * as AlertBarState from "@/components/alert-bar/AlertBar.state";
import { SELECT_ALL, SELECT_ALL_SLUG } from "@/sanctuary/typedefs";

export const OPEN_CREATE_USER_FORM = "userSettingsPage/OPEN_CREATE_USER_FORM";
export const openCreateUserForm = ({ email, firstName, lastName }) => ({
  type: OPEN_CREATE_USER_FORM,
  payload: {
    email,
    firstName,
    lastName,
  },
});

export const CLOSE_CREATE_USER_FORM = "userSettingsPage/CLOSE_CREATE_USER_FORM";
export const closeCreateUserForm = () => ({
  type: CLOSE_CREATE_USER_FORM,
});

export const OPEN_EDIT_USER_FORM = "userSettingsPage/OPEN_EDIT_USER_FORM";
export const openEditUserForm = ({ userId, userRole }) => ({
  type: OPEN_EDIT_USER_FORM,
  payload: {
    userId,
    userRole,
  },
});

export const CLOSE_EDIT_USER_FORM = "userSettingsPage/CLOSE_EDIT_USER_FORM";
export const closeEditUserForm = () => ({
  type: CLOSE_EDIT_USER_FORM,
});

export const OPEN_PERSONAL_INFO_FORM = "userSettingsPage/OPEN_PERSONAL_INFO_FORM";
export const openPersonalInfoForm = () => ({
  type: OPEN_PERSONAL_INFO_FORM,
});

export const CLOSE_PERSONAL_INFO_FORM = "userSettingsPage/CLOSE_PERSONAL_INFO_FORM";
export const closePersonalInfoForm = () => ({
  type: CLOSE_PERSONAL_INFO_FORM,
});

export const OPEN_TOAST_NOTIFICATION = "userSettingsPage/OPEN_TOAST_NOTIFICATION";
export const openToastNotification = ({ toastContent }) => ({
  type: OPEN_TOAST_NOTIFICATION,
  payload: { toastContent },
});

export const CLOSE_TOAST_NOTIFICATION = "userSettingsPage/CLOSE_TOAST_NOTIFICATION";
export const closeToastNotification = () => ({
  type: CLOSE_TOAST_NOTIFICATION,
});

export const RESET_SETTINGS_PAGE_WORKFLOW =
  "userSettingsPage/RESET_SETTINGS_PAGE_WORKFLOW";
export const resetSettingsPageWorkflow = () => ({
  type: RESET_SETTINGS_PAGE_WORKFLOW,
});

export const SUBMIT_EDIT_INTERNAL_USER_INFO_FORM =
  "userSettingsPage/SUBMIT_EDIT_INTERNAL_USER_INFO_FORM";
export const submitEditInternalUserInfoForm = () => ({
  type: SUBMIT_EDIT_INTERNAL_USER_INFO_FORM,
});

export const SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_SUCCESS =
  "userSettingsPage/SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_SUCCESS";
export const submitEditInternalUserInfoFormSuccess = () => ({
  type: SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_SUCCESS,
});

const SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_ERROR =
  "userSettingsPage/SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_ERROR";
export const submitEditInternalUserInfoFormFailure = (error) => ({
  type: SUBMIT_EDIT_INTERNAL_USER_INFO_FORM_ERROR,
  payload: { error },
});

export const FETCH_AGENCY_OPTIONS = "userSettingsPage/FETCH_AGENCY_OPTIONS";
export const fetchAgencyOptions = (agencies) => ({
  type: FETCH_AGENCY_OPTIONS,
  payload: {
    agencies,
  },
});

export const SET_SELECTED_AGENCIES = "userSettingsPage/SET_SELECTED_AGENCIES";
export const setSelectedAgencies = (agencies) => ({
  type: SET_SELECTED_AGENCIES,
  payload: {
    agencies,
  },
});

export const TOGGLE_SELECT_ALL_AGENCIES =
  "userSettingsPage/TOGGLE_SELECT_ALL_AGENCIES";
export const toggleSelectAllAgencies = (selectAll) => ({
  type: TOGGLE_SELECT_ALL_AGENCIES,
  payload: {
    selectAll,
  },
});

export const SELECT_AGENCY = "userSettingsPage/SELECT_AGENCY";
export const selectAgency = (agency) => ({
  type: SELECT_AGENCY,
  payload: {
    agency,
  },
});

export const SUBMIT_ADD_USER = "userSettingsPage/SUBMIT_ADD_USER";
export const submitAddUser = () => ({
  type: SUBMIT_ADD_USER,
});

export const SUBMIT_EDIT_USER = "userSettingsPage/SUBMIT_EDIT_USER";
export const submitEditUser = () => ({
  type: SUBMIT_EDIT_USER,
});

export const SUBMIT_PERSONAL_INFO = "userSettingsPage/SUBMIT_PERSONAL_INFO";
export const submitPersonalInfo = () => ({
  type: SUBMIT_PERSONAL_INFO,
});

export const SUBMIT_ACTIVATE_ADMINISTERED_USER =
  "userSettingsPage/SUBMIT_ACTIVATE_ADMINISTERED_USER";
export const submitActivateAdministeredUser = ({ userId }) => ({
  type: SUBMIT_ACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

export const SUBMIT_REACTIVATE_ADMINISTERED_USER =
  "userSettingsPage/SUBMIT_REACTIVATE_ADMINISTERED_USER";
export const submitReactivateAdministeredUser = ({ userId }) => ({
  type: SUBMIT_REACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

export const SUBMIT_DEACTIVATE_ADMINISTERED_USER =
  "userSettingsPage/SUBMIT_DEACTIVATE_ADMINISTERED_USER";
export const submitDeactivateAdministeredUser = ({ userId }) => ({
  type: SUBMIT_DEACTIVATE_ADMINISTERED_USER,
  payload: {
    userId,
  },
});

const INITIAL_STATE = {
  createUserFormIsOpen: false,
  editUserFormIsOpen: false,
  toastNotificationIsOpen: false,
  toastNotificationContent: "",
  userBeingAdded: S.Nothing,
  userBeingEdited: S.Nothing,
  userRoleBeingEdited: S.Nothing,
  userId: "",
  submitting: false,
  error: false,
  selectedAgencies: [],
  allSelected: false,
  allAgencyOptions: [],
};

export const _reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case OPEN_CREATE_USER_FORM:
      return {
        ...state,
        createUserFormIsOpen: true,
        userBeingAdded: {
          email: S.Just(payload.email),
          firstName: S.Just(payload.firstName),
          lastName: S.Just(payload.lastName),
        },
      };
    case CLOSE_CREATE_USER_FORM:
    case CREATE_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        createUserFormIsOpen: false,
      };
    case OPEN_EDIT_USER_FORM:
      return {
        ...state,
        editUserFormIsOpen: true,
        userBeingEdited: S.Just(payload.userId),
        userRoleBeingEdited: S.Just(payload.userRole),
      };
    case CLOSE_EDIT_USER_FORM:
    case EDIT_ADMINISTERED_USER_SUCCEEDED:
      return {
        ...state,
        editUserFormIsOpen: false,
        userBeingEdited: S.Nothing,
        userRoleBeingEdited: S.Nothing,
      };
    case OPEN_PERSONAL_INFO_FORM:
      return {
        ...state,
        personalInfoFormIsOpen: true,
      };
    case CLOSE_PERSONAL_INFO_FORM:
      return {
        ...state,
        personalInfoFormIsOpen: false,
      };
    case OPEN_TOAST_NOTIFICATION:
      return {
        ...state,
        toastNotificationIsOpen: true,
        toastNotificationContent: payload.toastContent,
      };
    case CLOSE_TOAST_NOTIFICATION:
      return {
        ...state,
        toastNotificationIsOpen: false,
      };
    case FETCH_AGENCY_OPTIONS:
      return {
        ...state,
        allAgencyOptions: (() => {
          const formattedAgencies = payload.agencies.map((agency) =>
            agency.slug
              ? {
                  slug: agency.slug,
                  name: agency.name,
                }
              : {
                  slug: agency.name.split(" ").join("-").toLowerCase(),
                  name: agency.name,
                }
          );

          return [{ slug: SELECT_ALL_SLUG, name: SELECT_ALL }, ...formattedAgencies];
        })(),
      };
    case SET_SELECTED_AGENCIES:
      return { ...state, selectedAgencies: payload.agencies };
    case TOGGLE_SELECT_ALL_AGENCIES:
      return {
        ...state,
        allSelected: payload.selectAll,
        selectedAgencies: payload.selectAll
          ? [{ slug: SELECT_ALL_SLUG, name: SELECT_ALL }, ...state.allAgencyOptions]
          : [],
      };
    case SELECT_AGENCY:
      return (() => {
        const isRemoving =
          state?.selectedAgencies?.find(
            (agency) => agency?.name === payload?.agency?.name
          ) ?? false;

        const updatedAgencies = isRemoving
          ? state.selectedAgencies.filter(
              (agency) => agency.name !== payload.agency.name
            )
          : state.selectedAgencies
          ? [...state.selectedAgencies, payload.agency]
          : [payload.agency];

        // When removing an agency while Select All is active, clear
        // Select All and strip the synthetic "all" entry so the save
        // payload sends individual agencies instead of the "all" slug.
        const shouldClearSelectAll = isRemoving && state.allSelected;

        return {
          ...state,
          allSelected: shouldClearSelectAll ? false : state.allSelected,
          selectedAgencies: shouldClearSelectAll
            ? updatedAgencies.filter((agency) => agency.slug !== SELECT_ALL_SLUG)
            : updatedAgencies,
        };
      })();
    case RESET_SETTINGS_PAGE_WORKFLOW:
      return {
        ...state,
        ...INITIAL_STATE,
      };
    default:
      return state;
  }
};

// selectors
const getState = (state) => state.userSettingsPage;
export const getCreateUserFormIsOpen = (state) =>
  getState(state).createUserFormIsOpen;
export const getEditUserFormIsOpen = (state) => getState(state).editUserFormIsOpen;
export const getPersonalInfoFormIsOpen = (state) =>
  getState(state).personalInfoFormIsOpen;
export const getUserBeingAdded = (state) => getState(state).userBeingAdded;
export const getUserBeingEdited = (state) => getState(state).userBeingEdited;
export const getUserRoleBeingEdited = (state) => getState(state).userRoleBeingEdited;
export const getToastNotificationIsOpen = (state) =>
  getState(state).toastNotificationIsOpen;
export const getToastNotificationContent = (state) =>
  getState(state).toastNotificationContent;

export const _mapStateToProps = (state) => ({
  ...state.userSettingsPage,
  createUserWorkflowIsOpen: getCreateUserFormIsOpen(state),
  editUserWorkflowIsOpen: getEditUserFormIsOpen(state),
  personalInfoWorkflowIsOpen: getPersonalInfoFormIsOpen(state),
  toastNotificationIsOpen: getToastNotificationIsOpen(state),
  toastNotificationContent: getToastNotificationContent(state),
  modificationStatuses: getUserModificationStatuses(state),
  currentUser: state.currentUser,
  userBeingAdded: getUserBeingAdded(state),
  userIsBeingChecked: getCheckAdministeredUserPending(state),
  userBeingEdited: getAdministeredUserBeingEdited(state),
  isPOSEnabled: getPointOfSaleEnabled(state),
  roleCombinations: getRoleCombinations(state),
});
export const _mapDispatchToProps = (dispatch) => ({
  closeCreateForm: () => dispatch(closeCreateUserForm()),
  closeEditForm: () => dispatch(closeEditUserForm()),
  closePersonalInfoForm: () => dispatch(closePersonalInfoForm()),
  closeToastNotification: () => dispatch(closeToastNotification()),
  fetchAgencyOptions: (agencies) => dispatch(fetchAgencyOptions(agencies)),
  setSelectedAgencies: (agencies) => dispatch(setSelectedAgencies(agencies)),
  toggleSelectAllAgencies: (allSelected) =>
    dispatch(toggleSelectAllAgencies(allSelected)),
  selectAgency: (agency) => dispatch(selectAgency(agency)),
  activateUser: (userId) => dispatch(submitActivateAdministeredUser({ userId })),
  deactivateUser: (userId) => dispatch(submitDeactivateAdministeredUser({ userId })),
  submitAddUser: () => dispatch(submitAddUser()),
  submitEditUser: () => dispatch(submitEditUser()),
  submitPersonalInfo: () => dispatch(submitPersonalInfo()),
});

export const USER_SETTINGS_PAGE_ALERT_BAR_ACTION =
  "userSettingsPage/USER_SETTINGS_PAGE_ALERT_BAR_ACTION";
export const userSettingsPageAlertBarAction = Nested.nestedAction(
  USER_SETTINGS_PAGE_ALERT_BAR_ACTION
);

export const ADD_INTERNAL_USER_ALERT_BAR_ACTION =
  "userSettingsPage/ADD_INTERNAL_USER_ALERT_BAR_ACTION";
export const addInternalUserAlertBarAction = Nested.nestedAction(
  ADD_INTERNAL_USER_ALERT_BAR_ACTION
);
export const ADD_INTERNAL_USER_INFO_FORM_ACTION =
  "userSettingsPage/ADD_INTERNAL_USER_INFO_FORM_ACTION";
export const addInternalUserFormAction = Nested.nestedAction(
  ADD_INTERNAL_USER_INFO_FORM_ACTION
);

export const EDIT_INTERNAL_USER_ALERT_BAR_ACTION =
  "userSettingsPage/EDIT_INTERNAL_USER_ALERT_BAR_ACTION";
export const editInternalUserAlertBarAction = Nested.nestedAction(
  EDIT_INTERNAL_USER_ALERT_BAR_ACTION
);
export const EDIT_INTERNAL_USER_INFO_FORM_ACTION =
  "userSettingsPage/EDIT_INTERNAL_USER_INFO_FORM_ACTION";
export const editInternalUserFormAction = Nested.nestedAction(
  EDIT_INTERNAL_USER_INFO_FORM_ACTION
);

export const PERSONAL_INFO_ALERT_BAR_ACTION =
  "userSettingsPage/PERSONAL_INFO_ALERT_BAR_ACTION";
export const personalInfoAlertBarAction = Nested.nestedAction(
  PERSONAL_INFO_ALERT_BAR_ACTION
);
export const PERSONAL_INFO_FORM_ACTION =
  "userSettingsPage/PERSONAL_INFO_FORM_ACTION";
export const personalInfoFormAction = Nested.nestedAction(PERSONAL_INFO_FORM_ACTION);

const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    forms: {
      addUserInfoForm: {
        ...InternalUserInfoForm,
        actionType: ADD_INTERNAL_USER_INFO_FORM_ACTION,
      },
      editUserInfoForm: {
        ...InternalUserInfoForm,
        actionType: EDIT_INTERNAL_USER_INFO_FORM_ACTION,
      },
      personalInfoForm: {
        ...InternalUserInfoForm,
        actionType: PERSONAL_INFO_FORM_ACTION,
      },
    },
    alertBars: {
      addUserInfoAlertBar: {
        ...AlertBarState,
        actionType: ADD_INTERNAL_USER_ALERT_BAR_ACTION,
      },
      editUserInfoAlertBar: {
        ...AlertBarState,
        actionType: EDIT_INTERNAL_USER_ALERT_BAR_ACTION,
      },
      personalInfoAlertBar: {
        ...AlertBarState,
        actionType: PERSONAL_INFO_ALERT_BAR_ACTION,
      },
      userSettingsPageAlertBar: {
        ...AlertBarState,
        actionType: USER_SETTINGS_PAGE_ALERT_BAR_ACTION,
      },
    },
  },
  "userSettingsPage"
);

const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
